import questionnairesData from '../data/questionnaires.json'

/**
 * doctorMatch.js — v242.75.0 (DOCTOR-MATCH-1)
 *
 * Which Orella doctors match this case, and WHICH OF THEIR SPECIALTIES earned
 * the match. Extracted from ShareScreen so the share-entry screen can show the
 * same list without a second implementation.
 *
 * The extraction is the point. TF-21 — "a cardiologist offered for an ear case"
 * — was not a filter bug: the doctor carried 29 specialties, one of them WAS
 * Otolaryngology (ENT), so the match was right and the card printed
 * `specialties[0]`, which was Cardiology. The field matched on and the field
 * displayed were different fields, so a correct result read as broken. A second
 * copy of this logic on a second screen is how that comes back.
 */

// v213.4 (C-A-166): the family-doc cluster — any of these counts as the same.
const FAMILY_DOC_CLUSTER = new Set([
  'general medicine',
  'family medicine',
  'internal medicine',
  'general practice',
  'family practice',
  'primary care',
])

const isFamilyDoc = (s) => FAMILY_DOC_CLUSTER.has(String(s || '').toLowerCase().trim())

/** Does one of the doctor's specialty strings satisfy one target specialty? */
export function specialtyMatchesTarget(docSpec, spec) {
  const docSpecLower = String(docSpec || '').toLowerCase().trim()
  if (!docSpecLower) return false
  const specLower = String(spec || '').toLowerCase().trim()

  if (docSpecLower === specLower) return true

  // ENT variations
  const ENT = new Set(['ent', 'otolaryngology (ent)', 'otolaryngology'])
  if (ENT.has(docSpecLower) && ENT.has(specLower)) return true

  if (isFamilyDoc(docSpecLower) && isFamilyDoc(specLower)) return true

  return false
}

/**
 * ★ THE ORDER THAT DECIDES THIS IS THE CASE'S, NOT THE DOCTOR'S.
 *
 * `matchingSpecialties` is ranked by relevance — for an ear case it reads
 * ['Otolaryngology (ENT)', 'General Medicine'] — so walk THAT list and take the
 * first target the doctor can satisfy. Walking the doctor's own array instead
 * labels a 29-specialty physician 'General Medicine' on an ear case purely
 * because it sits at index 5 while ENT sits at index 14: true, unhelpful, and
 * it under-sells the doctor the patient should pick.
 */
export function bestMatchingSpecialty(docSpecialties, matchingSpecialties) {
  for (const target of matchingSpecialties || []) {
    const hit = (docSpecialties || []).find(ds => specialtyMatchesTarget(ds, target))
    if (hit) return hit
  }
  return null
}

/**
 * Active, accepting doctors who match the case, each carrying the specialty
 * that earned the match as `matchedSpecialty`.
 */
export function matchDoctorsToCase(allDoctors, matchingSpecialties) {
  return (allDoctors || []).reduce((acc, d) => {
    if (d.status !== 'active' || d.accepting_patients === false) return acc
    const docSpecialties = Array.isArray(d.specialties) ? d.specialties : []
    if (docSpecialties.length === 0) return acc
    const matchedSpecialty = bestMatchingSpecialty(docSpecialties, matchingSpecialties)
    if (!matchedSpecialty) return acc
    acc.push({ ...d, matchedSpecialty })
    return acc
  }, [])
}


/* ── Which doctor specialties a case should be matched against ──
   Moved from ShareScreen with matchDoctorsToCase, because the two are one
   answer: this produces the RANKED target list, and bestMatchingSpecialty
   walks it in order. Splitting them across two files is how the ranking
   gets lost and a doctor gets labelled by their array order again. */
// Get matching specialties for a category from questionnaires data
export function getMatchingSpecialties(specialtyId, focusAreaId) {
  if (!specialtyId && !focusAreaId) {
    return ['General Medicine']
  }
  
  // Find the specialty in questionnaires data
  const specialty = questionnairesData.specialties?.find(s => 
    s.id === specialtyId || 
    s.id === focusAreaId?.split('-')[0] // Handle focus area IDs like "ear-pain" -> "ear"
  )
  
  // If specialty has matchingDoctorSpecialties defined, use those
  if (specialty?.matchingDoctorSpecialties && specialty.matchingDoctorSpecialties.length > 0) {
    console.log('[doctorMatch] Using specialty matchingDoctorSpecialties, count:', specialty.matchingDoctorSpecialties?.length)
    return specialty.matchingDoctorSpecialties
  }
  
  // Fallback to legacy hardcoded mapping
  return getLegacyMatchingSpecialties(specialtyId || focusAreaId)
}

// Legacy fallback mapping (kept for backwards compatibility)
function getLegacyMatchingSpecialties(categoryId) {
  if (!categoryId) return ['General Medicine']
  
  const normalizedId = categoryId.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '-')
  
  const LEGACY_MAPPING = {
    'ear': ['Otolaryngology (ENT)', 'General Medicine'],
    'eye': ['Ophthalmology', 'General Medicine'],
    'head-neck': ['Otolaryngology (ENT)'],
    'health-wellness': ['General Medicine', 'Endocrinology'],
    'muscle-joints': ['Orthopedics', 'Rheumatology', 'Pain Management', 'Neurology'],
    'nose': ['Otolaryngology (ENT)', 'Allergy & Immunology', 'General Medicine'],
    'pain': ['Pain Management', 'Neurology'],
    'skin': ['Dermatology'],
    'stomach-gut': ['Gastroenterology', 'General Medicine'],
    'throat': ['Otolaryngology (ENT)', 'General Medicine'],
    'urinary': ['Urology'],
  }
  
  // Try exact match first
  if (LEGACY_MAPPING[normalizedId]) {
    return LEGACY_MAPPING[normalizedId]
  }
  
  // Try partial match
  for (const [key, specialties] of Object.entries(LEGACY_MAPPING)) {
    if (normalizedId.includes(key) || key.includes(normalizedId)) {
      return specialties
    }
  }
  
  return ['General Medicine']
}
